/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — painel expandido de 1 contrato de venda (lazy load)
 */
import { useVendaContratoExpansao } from '../hooks/useObraAnalytics'
import { formatCurrency, formatDate, formatNumber, formatPercent } from '../../../../utils/obraAnalytics'

const SituacaoBadge = ({ situacao }) => {
    const map = {
        vencido: 'bg-rose-100 text-rose-700',
        a_vencer: 'bg-amber-100 text-amber-700',
        sem_venc: 'bg-gray-100 text-gray-600',
    }
    const label = { vencido: 'Vencido', a_vencer: 'A vencer', sem_venc: 'Sem venc.' }[situacao] || situacao
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${map[situacao] || map.sem_venc}`}>{label}</span>
}

const MiniTable = ({ headers, rows, empty }) => (
    <div className="overflow-x-auto">
        {rows.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">{empty}</p>
        ) : (
            <table className="min-w-full text-xs">
                <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                        {headers.map((h) => <th key={h} className="py-1.5 pr-3 font-medium">{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                            {row.map((cell, j) => <td key={j} className="py-1.5 pr-3 text-gray-600">{cell}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
)

const VendaContratoExpansao = ({ obraId, contrato, investidor, enabled, cached, onLoaded }) => {
    const { data, loading, error } = useVendaContratoExpansao(obraId, contrato, investidor, { enabled, cached, onLoaded })

    if (loading) {
        return <div className="px-4 py-5 text-xs text-gray-400 text-center">Carregando detalhes do contrato…</div>
    }
    if (error) {
        return <div className="px-4 py-5 text-xs text-rose-500 text-center">Não foi possível carregar este contrato.</div>
    }
    if (!data) return null

    const limite = 15

    return (
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-[10px] uppercase text-gray-400 font-semibold">VGV</p>
                    <p className="text-sm font-bold text-gray-800">{formatCurrency(data.vgv)}</p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-[10px] uppercase text-emerald-600 font-semibold">Recebido</p>
                    <p className="text-sm font-bold text-emerald-700">{formatCurrency(data.recebido)}</p>
                </div>
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-[10px] uppercase text-gray-400 font-semibold">Conversão</p>
                    <p className="text-sm font-bold text-gray-800">{formatPercent(data.pct_recebido)}</p>
                </div>
            </div>

            {data.unidades?.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1.5">
                        Unidades ({formatNumber(data.unidades.length)})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {data.unidades.map((u) => (
                            <span key={u} className="text-[11px] px-2 py-1 rounded-lg bg-white border border-gray-200 text-gray-600">{u}</span>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                        Parcelas recebidas
                        {data.recebidos.total > limite && (
                            <span className="text-gray-400 font-normal"> — exibindo {limite} de {formatNumber(data.recebidos.total)}</span>
                        )}
                    </p>
                    <MiniTable
                        headers={['Parcela', 'Tipo', 'Competência', 'Valor', 'Depositado']}
                        rows={(data.recebidos.parcelas || []).map((p) => [
                            p.parcela ?? '-',
                            p.tipo ?? '-',
                            formatDate(p.data_comp),
                            formatCurrency(p.vlr_parcela),
                            formatCurrency(p.total_dep),
                        ])}
                        empty="Nenhuma parcela recebida vinculada a este contrato."
                    />
                </div>
                <div className="rounded-xl bg-white border border-gray-100 p-3">
                    <p className="text-xs font-semibold text-gray-700 mb-2">
                        Parcelas a receber
                        {data.aReceber.total > limite && (
                            <span className="text-gray-400 font-normal"> — exibindo {limite} de {formatNumber(data.aReceber.total)}</span>
                        )}
                    </p>
                    <MiniTable
                        headers={['Parcela', 'Tipo', 'Vencimento', 'Valor', 'Situação']}
                        rows={(data.aReceber.parcelas || []).map((p) => [
                            p.parcela ?? '-',
                            p.tipo ?? '-',
                            formatDate(p.vencimento),
                            formatCurrency(p.vlr_parcela),
                            <SituacaoBadge key={`${p.parcela}-s`} situacao={p.situacao} />,
                        ])}
                        empty="Nenhuma parcela em aberto vinculada a este contrato."
                    />
                </div>
            </div>

            <p className="text-[10px] text-gray-400">
                Cliente: <b className="text-gray-600">{data.cliente}</b>
                {data.data_ven && data.data_ven !== '-' && <> · Venda em <b className="text-gray-600">{formatDate(data.data_ven)}</b></>}
            </p>
        </div>
    )
}

export default VendaContratoExpansao
